import { useTranslation } from 'react-i18next'
import { Eraser, FlaskConical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import { useClearTickerSamples, usePopulateTickerSamples, useTickerPermission, useTickerSampleCount } from '../hooks'

/**
 * "Populate ticker" and "Clear sample messages", for one recovery set (the Recovery Manager's set
 * page).
 *
 * Andrew, 25 Sep 2026: "There needs to be an option in recovery manager to populate the ticker.
 * This should only be available in development the same as system templates." So it is offered
 * only where the server says it exists - where shipped content is authored; anywhere else the
 * count answers 404 and nothing is rendered - and only to a user who may see the ticker. Populate
 * writes one message of each kind from the set's own objects and tasks, marked as samples; Clear
 * deletes those and nothing else.
 */
export function TickerSampleActions({ setId }: { setId: number }) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const allowed = useTickerPermission()
    const samples = useTickerSampleCount(setId, allowed)
    const populate = usePopulateTickerSamples(setId)
    const clear = useClearTickerSamples(setId)

    if (!allowed || !samples.isSuccess) return null
    const count = samples.data.count
    const busy = populate.isPending || clear.isPending

    const onPopulate = () =>
        populate.mutate(undefined, {
            onSuccess: (r) => toast({ title: t('ticker.samples.populated'), description: t('ticker.samples.populatedDescription', { count: r.written }) }),
            onError: (e) => toast({ variant: 'destructive', title: t('ticker.samples.populateFailed'), description: handleApiError(e) }),
        })
    const onClear = () =>
        clear.mutate(undefined, {
            onSuccess: (r) => toast({ title: t('ticker.samples.cleared'), description: t('ticker.samples.clearedDescription', { count: r.deleted }) }),
            onError: (e) => toast({ variant: 'destructive', title: t('ticker.samples.clearFailed'), description: handleApiError(e) }),
        })

    return (
        <div className="flex flex-col items-end gap-1" data-testid="ticker-sample-actions">
            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={onPopulate} disabled={busy} title={t('ticker.samples.hint')}>
                    <FlaskConical className="h-4 w-4 mr-2" />
                    {t('ticker.samples.populate')}
                </Button>
                <Button variant="ghost" size="sm" onClick={onClear} disabled={busy || count === 0}>
                    <Eraser className="h-4 w-4 mr-2" />
                    {t('ticker.samples.clear')}
                </Button>
            </div>
            <span className="text-xs text-muted-foreground" data-testid="ticker-sample-count">
                {t('ticker.samples.count', { count })}
            </span>
        </div>
    )
}
